import { type RampTheme, nudgeLightness } from './brandContrast'
import { type Oklch, hexToOklch, maxChromaFor, oklchToHex } from './oklch'

const RUNGS = ['50', '100', '200', '300', '400', '500', '600', '700', '800'] as const

type Rung = (typeof RUNGS)[number]

const TEAL_TEMPLATE: Record<RampTheme, Record<Rung, string>> = {
  light: {
    '50': '#eafaf9',
    '100': '#ccf2f1',
    '200': '#9ce6e7',
    '300': '#63d3d7',
    '400': '#2dbcc2',
    '500': '#12a6ad',
    '600': '#0c8990',
    '700': '#0e6e75',
    '800': '#11585e',
  },
  dark: {
    '50': '#07211f',
    '100': '#0c2f2e',
    '200': '#114a49',
    '300': '#0e6e75',
    '400': '#12a6ad',
    '500': '#2dbcc2',
    '600': '#63d3d7',
    '700': '#9ce6e7',
    '800': '#ccf2f1',
  },
}

const TONAL_CHROMA_RATIO = 0.55
const PLACEMENT_GAP = 0.125
const MIN_RUNG_STEP = 0.02
const ORDER_PROBES = Array.from({ length: 40 }, (_, index) => (index - 20) * 0.01)
const BADGE_TEXT_FLOOR = 4.5
const MIN_L = 0.02
const MAX_L = 0.98

export const SECONDARY_TOKEN_NAMES: readonly string[] = [
  ...RUNGS.map((key) => `--color-secondary-${key}`),
  '--color-chart-primary',
  '--color-chart-secondary',
]

function clampChroma(color: Oklch): Oklch {
  return { ...color, c: Math.min(color.c, maxChromaFor(color.l, color.h)) }
}

function tonalRung(template: Oklch, anchor: Oklch, primary: Oklch, brandHue: number): Oklch {
  return clampChroma({
    l: template.l,
    c: primary.c * TONAL_CHROMA_RATIO * (template.c / anchor.c),
    h: brandHue,
  })
}

function placeAwayFromHighlight(
  color: Oklch,
  highlightL: number,
  isOrdered: (l: number) => boolean
): Oklch {
  const candidates = [
    color.l,
    highlightL + PLACEMENT_GAP,
    highlightL - PLACEMENT_GAP,
    ...ORDER_PROBES.map((offset) => color.l + offset),
  ]
  const valid = candidates.filter(
    (l) => l >= MIN_L && l <= MAX_L && Math.abs(l - highlightL) >= PLACEMENT_GAP && isOrdered(l)
  )
  if (valid.length === 0) {
    return color
  }
  const closest = valid.reduce((best, l) =>
    Math.abs(l - color.l) < Math.abs(best - color.l) ? l : best
  )
  return clampChroma({ ...color, l: closest })
}

function placeHighlightRungs(ramp: Map<Rung, Oklch>, highlightL: number, theme: RampTheme): void {
  const rung500 = ramp.get('500')
  const rung400 = ramp.get('400')
  if (rung500 === undefined || rung400 === undefined) {
    return
  }
  const placed500 = placeAwayFromHighlight(rung500, highlightL, () => true)
  ramp.set('500', placed500)
  const lighterFirst = theme === 'light' ? 1 : -1
  ramp.set(
    '400',
    placeAwayFromHighlight(
      rung400,
      highlightL,
      (l) => (l - placed500.l) * lighterFirst >= MIN_RUNG_STEP
    )
  )
}

function enforceOrder(ramp: Map<Rung, Oklch>, theme: RampTheme): void {
  const lighterFirst = theme === 'light' ? 1 : -1
  const pivot = RUNGS.indexOf('500')
  for (let index = pivot - 1; index >= 0; index -= 1) {
    pushAway(ramp, RUNGS[index], RUNGS[index + 1], lighterFirst)
  }
  for (let index = pivot + 1; index < RUNGS.length; index += 1) {
    pushAway(ramp, RUNGS[index], RUNGS[index - 1], -lighterFirst)
  }
}

function pushAway(
  ramp: Map<Rung, Oklch>,
  key: Rung | undefined,
  neighbourKey: Rung | undefined,
  direction: number
): void {
  if (key === undefined || neighbourKey === undefined) {
    return
  }
  const color = ramp.get(key)
  const neighbour = ramp.get(neighbourKey)
  if (color === undefined || neighbour === undefined) {
    return
  }
  const boundary = neighbour.l + direction * MIN_RUNG_STEP
  const beyond = direction > 0 ? color.l >= boundary : color.l <= boundary
  if (!beyond) {
    ramp.set(key, clampChroma({ ...color, l: Math.min(MAX_L, Math.max(MIN_L, boundary)) }))
  }
}

export function brandSecondaryTokens(
  brand: Oklch,
  theme: RampTheme,
  primary500: string
): Record<string, string> {
  const primary = hexToOklch(primary500)
  const template = TEAL_TEMPLATE[theme]
  const anchor = hexToOklch(template['500'])
  const ramp = new Map<Rung, Oklch>()
  for (const key of RUNGS) {
    ramp.set(key, tonalRung(hexToOklch(template[key]), anchor, primary, brand.h))
  }
  placeHighlightRungs(ramp, primary.l, theme)
  const badgeText = ramp.get('700')
  const badgeBackground = ramp.get('100')
  if (badgeText !== undefined && badgeBackground !== undefined) {
    const nudged = nudgeLightness(badgeText, [
      { against: oklchToHex(badgeBackground), floor: BADGE_TEXT_FLOOR },
    ])
    ramp.set('700', hexToOklch(nudged))
  }
  enforceOrder(ramp, theme)
  const tokens: Record<string, string> = {}
  for (const key of RUNGS) {
    const color = ramp.get(key)
    if (color !== undefined) {
      tokens[`--color-secondary-${key}`] = oklchToHex(color)
    }
  }
  tokens['--color-chart-primary'] = primary500
  tokens['--color-chart-secondary'] = tokens['--color-secondary-500'] ?? primary500
  return tokens
}
